import React, { useEffect, useState, useRef } from "react";
import DetailCard from "../../components/centralMonitorCards/Cards/DetailCard";
//import { handleNotificationEvent } from '../../utils/notificationhandler'
import { connect } from "react-redux";
import { addKeyarData } from "../../redux/features/central_monitor/keyarData";
import {
  mergeLiveKeyarSamples,
  pruneStaleLiveSessions,
} from "../../redux/features/central_monitor/liveKeyarData";
import { addRealTimeBasicDetail } from "../../redux/features/central_monitor/basic_detail";
import { useKeyarLiveData } from "../../hooks/useKeyarLiveData";
import { isBasicAccount } from "../../utils/accountType";
import { normalizeKeyarFrame } from "../../utils/keyarFrameAdapter";
import { createSampleBatcher } from "../../utils/sampleBatcher";
import { getMonitoredPlaceIds } from "../../utils/childPlaces";
import axios from "axios";
import urls from "../../utils/url";
import { useDispatch } from "react-redux";
import { saveAs } from "file-saver";
import Cookie from "js-cookie";
import { isJson } from "../../utils/converter";
import Cookies from "js-cookie";

import CentralGraphLayout from "./central_monitor_graph/CentralGraphLayout";
import BackgroundTimer from "../../utils/backgroundTimer";

// One per place the logged-in account can see (Basic accounts, plus any
// account for a place in GRAPH_PLACE_IDS). Streams every actively-monitored
// patient at that place over a single connection (spec §2.2.A). If it never
// works, it stays silent forever and the REST poll below keeps covering
// that place for accounts that also use REST.
const PlaceLiveSocket = ({ placeId, onFrame, onStatusChange }) => {
  useKeyarLiveData({ mode: "place", id: placeId, onFrame, onStatusChange });
  return null;
};

// Places that get the new analytics UI (isGraphMode) and, for this
// WebSocket work, also get a live connection even on a non-Basic account —
// e.g. a Premium SuperAdmin overseeing Basic-tier nurses at these places.
const GRAPH_PLACE_IDS = [8562,8541, 8331, 8340, 8869, 5411, 8410, 8541, 8542, 8543, 8544, 8545, 8546, 8547, 8448, 8451, 8445, 8413, 8450, 8494, 8495, 8496, 8501, 8503, 8498, 8528, 8453, 8446, 8447, 8449, 8500, 8502, 8504, 8505, 8506, 8507, 8509, 8511, 8512, 8516, 8517, 8454, 8455, 8456, 8457, 8499, 8508, 8514, 8515, 8518, 8519, 8522, 8521]; // production place ids

// A place gets a live connection if the account is Basic, or if the place
// itself is known to have Basic-tier/new-app monitoring (GRAPH_PLACE_IDS).
const isLiveEnabledPlace = (placeId) => isBasicAccount() || GRAPH_PLACE_IDS.includes(placeId);

// Places still served by the REST live-tests-summary poll: everything that
// does NOT have a WebSocket. Basic accounts have none (all their devices are
// on the new app), and a GRAPH_PLACE_IDS place is WebSocket-only, so polling
// it too would just duplicate (and race) the live feed.
const getRestPlaceIds = () =>
  isBasicAccount() ? [] : getMonitoredPlaceIds().filter((id) => !GRAPH_PLACE_IDS.includes(id));

// No signal on the WS side tells us a session ended (spec §2.4/§8.7 only
// defines a 30s *device* inactivity timeout) — so a live-only entry is
// swept after this long with no new frame. Lowered from 35s so a card goes
// away soon after monitoring is ended on the tablet; keep it well above the
// ~1s frame cadence so a brief signal drop doesn't remove (and, via the
// grace-period cleanup in CentralGraphLayout, wipe the history of) a card.
const LIVE_STALE_THRESHOLD_MS = 15_000;
const LIVE_STALE_SWEEP_INTERVAL_MS = 2_000;

const CentralMonitor = ({ addKeyarData, mergeLiveKeyarSamples, pruneStaleLiveSessions }) => {
  const cookie = Cookie.get("access_token");
  const cookieTheme = Cookie.get("theme");
  const hospitalMappingRef = useRef({});
  // Latest WS status per place_id, kept outside React state since the
  // sweep effect below only reads it on its own timer tick, not on every
  // status change.
  const placeStatusRef = useRef({});

  // All places' frames funnel through one batcher: one store update per
  // 250ms flush instead of one per channel per frame.
  const mergeBatchRef = useRef(mergeLiveKeyarSamples);
  mergeBatchRef.current = mergeLiveKeyarSamples;
  const sampleBatcherRef = useRef(null);
  if (!sampleBatcherRef.current) {
    sampleBatcherRef.current = createSampleBatcher(
      (batch) => mergeBatchRef.current(batch),
      250,
      () => document.visibilityState === "hidden",
    );
  }
  useEffect(() => () => sampleBatcherRef.current.cancel(), []);

  const [hospitalDetail, setHospitalDetail] = useState(() => {
    const detail = Cookies.get("hospital_detail");
    return detail ? JSON.parse(detail) : null;
  });

  const rawBackendDumpRef = useRef([]);
  const downloadRawBackendJSON = () => {
    const blob = new Blob(
      [JSON.stringify(rawBackendDumpRef.current, null, 2)],
      { type: "application/json" },
    );

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "live-tests-summary-raw.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    window.downloadRawBackendJSON = downloadRawBackendJSON;

    return () => {
      delete window.downloadRawBackendJSON;
    };
  }, []);

  const [prevKeyarData, setPrevKeyarData] = useState([]);
  const [basicDetail, setBasicDetail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [delay, setDelay] = useState(5000);
  const [fetchDataPerSecsEnum, setfetchDataPerSecsEnum] = useState(5);
  const [currentDelayIndex, setCurrentDelayIndex] = useState(0);
  const [hasReloaded, setHasReloaded] = useState(false);
  const [logs, setLogs] = useState([]);
  const [theme, setTheme] = useState();
  const [minSessionTime, setMinSessionTime] = useState({});
  const [isGraphMode, setIsGraphMode] = useState(false);
  const [isLoadingGraphMode, setIsLoadingGraphMode] = useState(true);

  // Tab-switch UX: rather than leave the grid looking frozen on whatever it
  // last showed while the tab was backgrounded (browsers throttle/can
  // freeze hidden tabs, stalling the WS), surface a brief "syncing" overlay
  // when the user returns after a real away period, until live data is
  // actually flowing again. A quick alt-tab (< AWAY_THRESHOLD_MS) is
  // ignored so this doesn't flicker on every glance away.
  const [isResyncing, setIsResyncing] = useState(false);
  const isResyncingRef = useRef(false);
  const hiddenAtRef = useRef(null);
  // Wall-clock time of the newest frame from any place — lets the tab-return
  // handler tell "data kept flowing while hidden" from "feed went quiet".
  const lastFrameWallRef = useRef(0);
  const resyncSafetyTimeoutRef = useRef(null);

  const setResyncing = (value) => {
    if (isResyncingRef.current === value) return;
    isResyncingRef.current = value;
    setIsResyncing(value);
  };

  useEffect(() => {
    const AWAY_THRESHOLD_MS = 3000;
    // Belt-and-suspenders: clear the overlay on its own even if no frame
    // arrives, so a slow reconnect never leaves it stuck up indefinitely.
    const MAX_RESYNC_DURATION_MS = 8000;
    const FEED_FRESH_MS = 5000;

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }

      const hiddenFor = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
      hiddenAtRef.current = null;
      if (hiddenFor < AWAY_THRESHOLD_MS) return;

      // Frames are stored the moment they arrive even while hidden (see the
      // sample batcher), so if one landed in the last few seconds the grid is
      // already current — show it straight away, no "syncing" overlay.
      if (Date.now() - lastFrameWallRef.current < FEED_FRESH_MS) return;

      setResyncing(true);
      if (resyncSafetyTimeoutRef.current) clearTimeout(resyncSafetyTimeoutRef.current);
      resyncSafetyTimeoutRef.current = setTimeout(() => {
        setResyncing(false);
        resyncSafetyTimeoutRef.current = null;
      }, MAX_RESYNC_DURATION_MS);
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (resyncSafetyTimeoutRef.current) clearTimeout(resyncSafetyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (cookieTheme) {
      setTheme(cookieTheme);
    }
  }, [cookieTheme]);

  useEffect(() => {
    const detail = Cookies.get("hospital_detail");
    setHospitalDetail(detail ? JSON.parse(detail) : null);
  }, []);

  useEffect(() => {
    try {
      const childPlacesCookie = Cookie.get("child_places");

      if (!childPlacesCookie) {
        console.log("child_places not ready yet");
        return;
      }

      let allUserPlaceIDs = [];

      if (childPlacesCookie) {
        const places = JSON.parse(childPlacesCookie);
        allUserPlaceIDs = places.map((p) => Number(p.id));
      }

      console.log("child_places raw:", childPlacesCookie);
      console.log("parsed IDs:", allUserPlaceIDs);

      const userHasGraphPlace = GRAPH_PLACE_IDS.some((id) =>
        allUserPlaceIDs.includes(id),
      );

      console.log("USER PLACE IDS:", allUserPlaceIDs);
      console.log("HAS GRAPH PLACE:", userHasGraphPlace);
      console.log("SUBSCRIPTION:", hospitalDetail?.subscription_type);

      setIsGraphMode( userHasGraphPlace);

      setIsLoadingGraphMode(false);
    } catch (err) {
      console.warn("❌ Error checking graph mode", err);
      setIsLoadingGraphMode(false);
    }
  }, [hospitalDetail]);

  const handleNotificationEvent = (e) => {
    const dataContent = JSON.parse(e.data.payload.content);
    //console.log(dataContent)
    if (dataContent.type == "patient_keyar_data") {
      if (
        e.data.payload != null &&
        e.data.payload != undefined &&
        dataContent !== null &&
        dataContent != undefined
      ) {
        let data = JSON.parse(e.data.payload.content);
        if (data.content.message == "keyar_test_started") {
          setTimeout(() => {
            setIsMonitoring(true);
          }, fetchDataPerSecsEnum * 1000);
        }
      }
    }
  };

  // useEffect(() => {
  //   fetchEnum()
  // },[])

  // useEffect(() => {
  //   // Skip API calls on localhost - mock data will be used instead
  //   if (isLocal) {
  //     console.log("Localhost detected - skipping API calls in CentralMonitor");
  //     return;
  //   }

  //   let intervalId;
  //   fetchLatestKeyarData()

  //   if (isMonitoring){
  //     intervalId = setInterval(fetchLatestKeyarData, 10000);
  //   }
  //   return () => clearInterval(intervalId);

  // },[isMonitoring, isLocal])

  // useEffect(() => {
  //     // Skip API calls on localhost
  //     if (isLocal) {
  //       return;
  //     }

  //     if (!isMonitoring) {
  //       const timerId = setTimeout(fetchLatestKeyarData, delay);
  //       return () => clearTimeout(timerId);
  //     }
  //   }, [isMonitoring, delay, currentDelayIndex, isLocal]);
  
  useEffect(() => {
    try {
      const childPlacesCookie = Cookie.get("child_places");

      if (childPlacesCookie) {
        const places = JSON.parse(childPlacesCookie);

        const mapping = {};
        places.forEach((place) => {
          mapping[String(place.id)] = {
            name: place.name || "",
            logo: place.logo || null,
          };
        });

        hospitalMappingRef.current = mapping;
      }
    } catch (err) {
      console.error("Failed to load hospital mapping", err);
    }
  }, []);

  useEffect(() => {
    // Basic accounts and GRAPH_PLACE_IDS places are WebSocket-only; the REST
    // poll only runs while at least one watched place still needs it.
    if (getRestPlaceIds().length === 0) return undefined;

    // 🕒 Use a Web Worker timer to prevent throttling in background tabs.
    // Falls back to fetchDataPerSecsEnum (default 10s) while monitoring.
    const intervalMs = isMonitoring ? 10000 : delay;
    const timer = new BackgroundTimer(intervalMs, fetchLatestKeyarData);

    timer.start();

    return () => timer.stop();
  }, [isMonitoring, delay]);

  // Sweeps out any live-only (WebSocket) entry with no new frame in
  // LIVE_STALE_THRESHOLD_MS — nothing on the WS side signals a session
  // ended, so this is the only thing that removes a stale card for a
  // patient REST never knew about in the first place. Skipped entirely
  // whenever any watched place isn't confirmed "open" — a session that's
  // merely mid-reconnect (cycling through the backoff ladder) shouldn't
  // get pruned just because it's been quiet, since it may resume moments
  // later on its own. Biased toward under-pruning: a stale card lingers a
  // little longer in the rare case this is overly cautious, rather than a
  // recovering one vanishing while it's about to come back.
  useEffect(() => {
    const interval = setInterval(() => {
      // Read fresh each tick rather than closing over placeIdsToUse — the
      // account's watched places can change over time, and a stale
      // entry left behind in placeStatusRef for a place that's no longer
      // watched must not be allowed to block the sweep forever.
      const activePlaceIds = getMonitoredPlaceIds().filter(isLiveEnabledPlace);
      const allOpen = activePlaceIds.every(
        (placeId) => placeStatusRef.current[placeId] === "open",
      );
      if (!allOpen) return;

      pruneStaleLiveSessions({ thresholdMs: LIVE_STALE_THRESHOLD_MS, now: Date.now() });
    }, LIVE_STALE_SWEEP_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [pruneStaleLiveSessions]);

  const placeIdsToUse = getMonitoredPlaceIds();

  const fetchLatestKeyarData = async () => {
    const startTime = new Date();
    let placeIdsToUse = [];

    try {
      placeIdsToUse = getRestPlaceIds();

      if (placeIdsToUse.length === 0) {
        return;
      }

      const placeQuery = placeIdsToUse.join(",");

      const res = await axios.get(
        `${urls.DEV_URL}v1/tesla/live-tests-summary/?place_ids=${placeQuery}`,
        {
          headers: {
            "Request-ID": "allow|me",
            "Content-Type": "application/json",
            Authorization: `Bearer ${cookie}`,
          },
        },
      );

      const json_data = isJson(res.data);
      const data = json_data.response;

      rawBackendDumpRef.current.push({
        fetched_at_local: new Date().toLocaleString("en-IN", { hour12: false }),
        fetched_at_epoch: Date.now(),
        response: data,
      });

      if (rawBackendDumpRef.current.length > 1000) {
        rawBackendDumpRef.current.shift();
      }

      if (data.live_tests) {
        Object.values(data.live_tests).forEach((patient, idx) => {
          // console.log(`Patient ${idx}:`, patient.patient_name);
          if (patient.keyar_data_sets && patient.keyar_data_sets[0]) {
            // console.log(
            //   "  keyar_params available:",
            //   patient.keyar_data_sets[0].keyar_params.map(
            //     (p) => p.param_type || p.param
            //   )
            // );
          }
        });
      }

      if (data && data.live_tests == undefined) {
        updateDelay();
        setIsMonitoring(false);
        addKeyarData([]);
        setMinSessionTime({});
      } else {
        setIsMonitoring(true);
        resetDelay();
        const patient_keyar_data = data.live_tests;

        const keyar_data = Object.values(patient_keyar_data).map((obj) => {
          if (!obj.keyar_data_sets || obj.keyar_data_sets.length === 0) {
            console.error(
              `Missing keyar_data_sets for patient: ${obj.patient_name}`,
            );
            return null;
          }
          const sessionTime = obj.keyar_data_sets[0].data_time;

          const prevMinTime =
            minSessionTime[obj.patient_name] ?? Number.MAX_SAFE_INTEGER;

          const updatedMinTime = Math.min(prevMinTime, sessionTime);

          setMinSessionTime((prevMinTimes) => ({
            ...prevMinTimes,
            [obj.patient_name]: updatedMinTime,
          }));

          const placeId = obj.keyar_data_sets[0].place_id;
          const hospitalInfo = hospitalMappingRef.current[placeId] || {
            name: "",
            logo: null,
          };
          console.log("FULL OBJ:", obj);
          console.log("RAW NAME:", obj.patient_name);
          const patientName =
            obj.patient_name ||
            obj.keyar_data_sets[0]?.patient_name ||
            `Patient ${obj.keyar_data_sets[0].patient_id}`;

          return {
            patientName,
            keyarData: obj.keyar_data_sets[0].keyar_params,
            patientID: obj.keyar_data_sets[0].patient_id,
            session_time: updatedMinTime,
            place_id: placeId,
            hospitalName: hospitalInfo.name,
            hospitalLogo: hospitalInfo.logo,
          };
        });

        const cleanedKeyarData = keyar_data.filter(Boolean);
        // console.log(`fetchLatestKeyarData: cleanedKeyarData.length=${cleanedKeyarData.length}`);
        if (cleanedKeyarData.length === 0) {
          console.warn(
            "CentralMonitor: no valid keyar_data entries found in API response",
          );

          if (!window.__centralMonitorRetryScheduled) {
            window.__centralMonitorRetryScheduled = true;
            // console.log('CentralMonitor: scheduling one retry fetch in 1500ms');
            setTimeout(() => {
              fetchLatestKeyarData();
            }, 1500);
          }
        }
        addKeyarData(cleanedKeyarData);
      }
    } catch (err) {
      setMinSessionTime({});

      updateDelay();
      setLoading(false);
    }
  };

  const updateDelay = () => {
    const delaysInSecs = [
      5,
      5,
      10,
      15,
      30,
      60,
      2 * 60,
      5 * 60,
      10 * 60,
      15 * 60,
      30 * 60,
      60 * 60,
    ];
    let nextDelayIndex = currentDelayIndex + 1;

    if (nextDelayIndex >= delaysInSecs.length) {
      nextDelayIndex = delaysInSecs.length - 1;
    }

    setCurrentDelayIndex(nextDelayIndex);
    const nextDelay = delaysInSecs[nextDelayIndex] * 1000;
    setDelay(nextDelay);
  };

  const resetDelay = () => {
    setDelay(5000);
    setCurrentDelayIndex(0);
  };

  //console.log(logs)

  const exportLogs = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," + logs.map((e) => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    saveAs(encodedUri, "logs.csv");
  };

  const fetchEnum = async () => {
    await axios
      .get(
        `${urls.DEV_URL}tesla/app-enums/?source_app=Daksh&categories=keyar&`,
        {
          headers: {
            "Request-ID": "allow|me",
            "Content-Type": "application/json",
            Authorization: `Bearer ${cookie}`,
          },
        },
      )
      .then((res) => {
        console.log(res.data.response.enums);
      })
      .catch((err) => {
        console.log("Enum API Failed", err);
        //fetchLatestKeyarData()
        setLoading(false);
        //setError("Invalid Credentials")
        //setIsLoading(false)
      });
  };

  navigator.serviceWorker.onmessage = (e) => {
    handleNotificationEvent(e);
  };

  // console.log(keyar_data)

  //     return(
  //       <div style={{background: '#0E0E0E',  margin: 0, padding: 0, height:`100vh`}}>
  //  <DetailCard />
  //       </div>
  //     )

  // CMS - CHANGE
  return (
    <div
      style={{
        background:
          theme === "light-theme" && isGraphMode ? "#E8E8E8" : "#0E0E0E",
        margin: 0,
        padding: 0,
        height: `100vh`,
      }}
    >
      {/* {console.log("🔍 Rendering: ", isGraphMode ? "CentralGraphLayout" : "DetailCard", " | isGraphMode =", isGraphMode)} */}
      {isGraphMode ? <CentralGraphLayout isResyncing={isResyncing} /> : <DetailCard />}
      {placeIdsToUse
        .filter(isLiveEnabledPlace)
        .map((placeId) => (
          <PlaceLiveSocket
            key={placeId}
            placeId={placeId}
            onFrame={(frame) => {
              lastFrameWallRef.current = Date.now();
              // Frames don't carry place_id, but this socket is for exactly one
              // place — stamp it so the grid can show the hospital name/logo
              // (looked up from child_places, as it does for REST patients).
              sampleBatcherRef.current.push(
                normalizeKeyarFrame(frame).map((sample) => ({ ...sample, place_id: placeId })),
              );
              // Real data is flowing again — the overlay (if up) has done its job.
              if (isResyncingRef.current) {
                setResyncing(false);
                if (resyncSafetyTimeoutRef.current) {
                  clearTimeout(resyncSafetyTimeoutRef.current);
                  resyncSafetyTimeoutRef.current = null;
                }
              }
            }}
            onStatusChange={(status) => {
              placeStatusRef.current[placeId] = status;
            }}
          />
        ))}
    </div>
  );
};

const mapStateToProps = (state) => ({
  keyarData: state.keyarData.keyarData,
  liveBasicDetail: state.liveBasicDetail.liveBasicDetail,
});

const mapDispatchToProps = {
  addKeyarData,
  mergeLiveKeyarSamples,
  pruneStaleLiveSessions,
  addRealTimeBasicDetail,
};

export default connect(mapStateToProps, mapDispatchToProps)(CentralMonitor);
